import { createBrowserHistory, type RouterHistory } from "@tanstack/react-router";
import { parseHref } from "@tanstack/history";

// The one seam that knows what a session address looks like on each surface.
//
// Electron loads the app from a file-backed shell, so the address of the one
// fronted session lives inside the URL hash (`#/env/thread?world=k`); every
// browser surface routes in the pathname. `main.tsx` selects the router
// history from `appHistoryKindForShell`, and the World store parses/stamps
// addresses through the hash helpers below so both shells speak one URL
// grammar.

/** History kind per surface: Electron shells route inside the hash. */
export function appHistoryKindForShell(isElectron: boolean): "hash" | "browser" {
  return isElectron ? "hash" : "browser";
}

/** Search portion of a hash-shell route (`#/path?query`), or null outside one. */
export function hashRouteSearch(hash: string): string | null {
  if (!hash.startsWith("#/")) return null;
  const queryIndex = hash.indexOf("?");
  return queryIndex === -1 ? "" : hash.slice(queryIndex + 1);
}

/** Path portion of a hash-shell route (`#/path?query`), or null outside one. */
export function hashRoutePath(hash: string): string | null {
  if (!hash.startsWith("#/")) return null;
  const queryIndex = hash.indexOf("?");
  return hash.slice(1, queryIndex === -1 ? undefined : queryIndex) || "/";
}

/** Keep the shell query separate from the route query in an installed app.
 * TanStack's default hash parser concatenates them, corrupting the final
 * route value when both contain a query. Inner route values take precedence.
 */
export function createAppHistory(isElectron: boolean): RouterHistory {
  if (appHistoryKindForShell(isElectron) === "browser") return createBrowserHistory();
  return createBrowserHistory({
    parseLocation: () => {
      const route = new URL(window.location.hash.slice(1) || "/", "https://route.invalid");
      const search = new URLSearchParams(window.location.search);
      for (const [key, value] of route.searchParams) search.set(key, value);
      const query = search.toString();
      const routeSearch = query ? `?${query}` : "";
      // Native World/Local hash navigation has no history state. Use the
      // library parser to supply the router's key/index contract as well.
      return parseHref(`${route.pathname}${routeSearch}${route.hash}`, window.history.state);
    },
    createHref: (href) => `${window.location.pathname}${window.location.search}#${href}`,
  });
}
